import { Fragment, useMemo } from 'react'
import { MarketingDrawer, PageTitle } from '@app/components/general'
import { metaSetter } from '@app/utils'
import { useRouter } from 'next/router'
import { Header2, Header3, Header4 } from '@app/components/general/header'
import { Link } from '@app/components/stateless/typo/link'
import { apiRoute } from '@app/configs/api-route'
import { useAuthContext } from '@app/components/providers/auth'
import { routeParseAbsolute } from '@app/lib/router-handler'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import Trans from 'next-translate/Trans'
import { AppLayout } from '@app/components/app-layout'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { blueLink } from '@app/components/stateless/typo/link-style'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/download-data-export',
    }),
  }
}

const downloadStyles =
  'capitalize inline-block w-full text-sm text-center border border-blue-600 text-blue-600 dark:border-blue-600 rounded px-8 py-2 hover:text-blue-400 hover:border-blue-400 dark:hover:border-blue-400 hover:no-underline md:w-auto md:min-w-[244px]'

// download the contents real time. TODO: validate hash and get downloads based on hash and transltae for rtl
function DownloadDataExport() {
  const router = useRouter()
  const { account } = useAuthContext()
  const { t } = useTranslation()

  const { url } = router.query ?? { url: 'Not Found' }

  const encodedUrl = useMemo(
    () => (url ? encodeURIComponent(typeof url === 'string' ? url : '') : ''),
    [url]
  )

  // TODO: export data langauge i8n

  const dataExportText = t('dashboard:export-data')
  const pagesText = t('dashboard:pages')
  const issuesText = t('dashboard:issues')
  const analyticsText = t('dashboard:analytics')

  const exportOnly = dataExportText.split(' ')[0]

  return (
    <Fragment>
      <MarketingDrawer
        marketingLinks={false}
        authenticated={account?.authed}
        backButton
        initClosed
      >
        <div className='container px-6 mx-auto py-12'>
          <PageTitle>{`Export Data: ${url || t('dashboard:page')}`}</PageTitle>

          <div className='pb-5'>
            <Header2 className='text-lg md:text-lg lg:text-lg xl:text-lg sr-only'>
              {t('download-data-export:subheader')}
            </Header2>
            <p className='text-gray-800 dark:text-gray-200'>
              {t('download-data-export:subheader-d')}
            </p>
          </div>

          {account.activeSubscription ? (
            <div className='space-y-2'>
              <div>
                <Header3 className='capitalize'>{analyticsText}</Header3>
                <Link
                  href={`${apiRoute}/website/export?csv=true&q=${encodedUrl}&data=analytics`}
                  className={downloadStyles}
                >
                  {exportOnly} {analyticsText} CSV
                </Link>
              </div>
              <div>
                <Header3 className='capitalize'>{pagesText}</Header3>
                <Link
                  href={`${apiRoute}/website/export?csv=true&q=${encodedUrl}&data=pages`}
                  className={downloadStyles}
                >
                  {exportOnly} {pagesText} CSV
                </Link>
              </div>
              <div>
                <Header3 className='capitalize'>{issuesText}</Header3>
                <Link
                  href={`${apiRoute}/website/export?csv=true&q=${encodedUrl}&data=issues`}
                  className={downloadStyles}
                >
                  {exportOnly} {issuesText} CSV
                </Link>
              </div>

              <div>
                <Header3 className='capitalize'>PDF</Header3>
                {account.pdfEnabled ? (
                  <Link
                    href={`${apiRoute}/website/export-pdf?q=${encodedUrl}`}
                    className={downloadStyles}
                  >
                    {exportOnly} PDF
                  </Link>
                ) : (
                  <Trans
                    i18nKey='download-data-export:pdf-req'
                    components={[
                      <Paragraph key={'p1'} />,
                      <Link
                        href={`/settings#pdf-audits`}
                        className={blueLink}
                        key={'link1'}
                      />,
                    ]}
                  />
                )}
              </div>

              <div className='py-2'>
                <Header4>{t('data-parse:header')}</Header4>
                <Paragraph>{t('data-parse:subtitle-details')}</Paragraph>
                <div className='pt-2'>
                  <Link
                    href={'/visualizer'}
                    className={'underline text-blue-600 hover:text-blue-400'}
                  >
                    {t('data-parse:header')}
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <Trans
              i18nKey='download-data-export:active-sub'
              components={[
                <Paragraph key={'p1'} className='text-xl' />,
                <Link
                  href={routeParseAbsolute(
                    account.authed ? '/payments' : '/register',
                    router.locale
                  )}
                  className={'hover:text-blue-600'}
                  key={'p2'}
                />,
              ]}
              values={{
                route: t(account.authed ? 'common:register' : 'menu:payments'),
              }}
            />
          )}
        </div>
      </MarketingDrawer>
    </Fragment>
  )
}

DownloadDataExport.getLayout = AppLayout

// TODO: SSR GET HASH OF PAGES AND APPEND TO CLIENT DATA
export default metaSetter(
  { DownloadDataExport },
  {
    gql: true,
    translation: 'download-data-export',
  }
)
